import { visit } from 'unist-util-visit';
import { Plugin } from 'unified';
import { Node } from 'unist';

/** Our custom details node type. */
interface DetailsNode extends Node {
  type: 'details';
  summary: string;
  summaryLines: string[];
  summaryLineNodes: Node[][];
  children: Node[];
}

function getTextFromNode(node: any): string {
  if (node.value) return node.value;
  if (node.children) return node.children.map(getTextFromNode).join('');
  return '';
}

function splitNodesIntoLines(nodes: any[]): Node[][] {
  const lines: Node[][] = [[]];

  for (const node of nodes) {
    if (node.type !== 'text') {
      lines[lines.length - 1].push(node);
      continue;
    }

    const parts = node.value.split('\n');
    parts.forEach((part: string, index: number) => {
      if (index > 0) lines.push([]);
      if (part.length > 0) lines[lines.length - 1].push({ ...node, value: part });
    });
  }

  return lines.filter(line => line.length > 0);
}

/**
 * This plugin expects you to write Markdown like:
 *
 * :::details "My Summary"
 * Here is some text.
 * :::
 *
 * If you're using remarkDirective, that becomes a node of type "containerDirective"
 * with node.name === "details".
 */
export const remarkDetails: Plugin = () => {
  return (tree) => {
    visit(tree as any, (node, index, parent) => {
      if (node.type === 'containerDirective' && (node as any).name === 'details') {
        // cast to be able to access .children
        const container = node as any;
        const children = container.children || [];

        let summary = '';
        let summaryLines: string[] = [];
        let summaryLineNodes: Node[][] = [];
        // if the first child is a paragraph, treat it as a summary
        if (children.length > 0 && children[0].type === 'paragraph') {
          const firstParagraph = children[0];
          // gather its text, preserving any soft line breaks (\n) so that
          // multi-line summaries can render on multiple lines.
          summaryLineNodes = splitNodesIntoLines(firstParagraph.children || []);
          const raw = summaryLineNodes
            .map(line => line.map(getTextFromNode).join('').trim())
            .join('\n')
            .trim();
          summary = raw;
          summaryLines = raw
            .split('\n')
            .map((line: string) => line.trim())
            .filter((line: string) => line.length > 0);
          // remove that from the children array
          children.shift();
        }

        const detailsNode: DetailsNode = {
          type: 'details',
          summary,
          summaryLines,
          summaryLineNodes,
          children
        };

        if (parent && typeof index === 'number') {
          parent.children[index] = detailsNode;
        }
      }
    });
  };
};